import type { ReactNode } from "react";
import { memo, useMemo } from "react";
import type { Config } from "tailwindcss";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import type { ResponsiveFrameProps } from "@circle-react-shared/ResponsiveFrame";
import { ResponsiveFrame } from "@circle-react-shared/ResponsiveFrame";
import builderTailwindConfig from "../styles/tailwind.builder.config";
import { IFRAME_DATA_TEST_ID, IFRAME_ID } from "./constants";

const mountPointId = "tailwind-frame-mount";
const mountPointSelector = `#${mountPointId}`;

interface InitialContent {
  bodyEnd?: string;
  bodyStart?: string;
  headEnd?: string;
  headStart?: string;
  tailwindConfig?: Partial<Config>;
}

const defaultHead = `
<style>
  @font-face {
    font-family: InterVariable;
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url("https://rsms.me/inter/font-files/InterVariable.woff2?v=4.0") format("woff2");
  }
</style>
`;

const defaultTailwindConfig = {
  theme: {
    fontFamily: {
      sans: ["InterVariable", "ui-sans-serif", "system-ui", "sans-serif"],
    },
  },
};

export const getInitialContent = ({
  headStart = defaultHead,
  headEnd = "",
  bodyStart = "",
  bodyEnd = "",
  tailwindConfig = defaultTailwindConfig,
}: InitialContent) => `
  <!DOCTYPE html>
  <html>
    <head>
      ${headStart}

      <style>
        html, body, ${mountPointSelector} {
          width: 100%;
          height: 100%;
        }

        ${mountPointSelector} .frame-content {
          width: 100%;
          height: 100%;
          position: relative;
        }
      </style>

      <!-- The below script comes from https://unpkg.com/tailwindcss-cdn@3.4.10/tailwindcss-with-all-plugins.js -->
      <script src="${window.TAILWIND_ASSET_PATH || "/assets/tailwindcss-with-all-plugins.js"}"></script>

      <script>
        tailwind.config = ${JSON.stringify(tailwindConfig)};
      </script>

      ${headEnd}
    </head>
    <body>
      ${bodyStart}
      <div id="${mountPointId}"></div>
      ${bodyEnd}
    </body>
  </html>
`;

export interface TailwindFrameProps
  extends ResponsiveFrameProps,
    InitialContent {
  children: ReactNode;
}

/** An iframe that uses a just-in-time Tailwind CSS compiler for it's styles */
export const TailwindFrame = memo(
  ({
    children,
    className,
    headStart,
    headEnd,
    bodyStart,
    bodyEnd,
    tailwindConfig = builderTailwindConfig,
    ...rest
  }: TailwindFrameProps) => {
    const initialContent = useMemo(
      () =>
        getInitialContent({
          headStart,
          headEnd,
          bodyStart,
          bodyEnd,
          tailwindConfig,
        }),
      [headStart, headEnd, bodyStart, bodyEnd, tailwindConfig],
    );
    const isDragging = useBuilderStore(state => state.isDragging);

    return (
      <ResponsiveFrame
        id={IFRAME_ID}
        initialContent={initialContent}
        mountTarget={mountPointSelector}
        data-testid={IFRAME_DATA_TEST_ID}
        className={classNames(className, {
          // https://github.com/clauderic/dnd-kit/issues/1374
          ["pointer-events-none"]: isDragging,
        })}
        style={{ position: "relative" }}
        {...rest}
      >
        {children}
      </ResponsiveFrame>
    );
  },
);
TailwindFrame.displayName = "BuilderUITailwindFrame";
